import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { can } from "@/lib/permissions";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Plus, Trash2, ScrollText } from "lucide-react";

const CHANNELS = ["TV", "Paid Search", "Billboard", "Radio", "Social", "Directory", "Print", "Other"];
const empty = {
  title: "", jurisdiction: "Georgia", channel: "TV", vendor_id: "none", approver: "",
  approval_date: "", ran_from: "", ran_to: "", disclaimers: "", notes: "",
};

export default function Compliance() {
  const [items, setItems] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const { user } = useAuth();
  const writable = can(user?.role, "compliance:write");

  const load = () => {
    api.get("/compliance").then((r) => setItems(r.data));
    api.get("/vendors").then((r) => setVendors(r.data));
  };
  useEffect(() => {
    load();
  }, []);

  const upd = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const vName = (id) => vendors.find((v) => v.id === id)?.name || "—";

  const save = async () => {
    if (!form.title) return toast.error("Title is required");
    await api.post("/compliance", {
      ...form,
      vendor_id: form.vendor_id === "none" ? null : form.vendor_id,
      approval_date: form.approval_date || null,
      ran_from: form.ran_from || null,
      ran_to: form.ran_to || null,
    });
    toast.success("Archived");
    setOpen(false);
    setForm(empty);
    load();
  };

  const remove = async (id) => {
    await api.delete(`/compliance/${id}`);
    load();
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-8">
        <div>
          <p className="font-mono-data text-xs uppercase tracking-[0.2em] text-[#B54747]">Jurisdiction-configurable</p>
          <h1 className="font-display text-4xl font-light mt-1">Advertising Compliance Archive</h1>
          <p className="text-sm text-zinc-500 mt-2 max-w-2xl">
            Retain approved ads, approval dates, disclaimers and run windows. Configurable by jurisdiction — this is a
            record-keeping tool, not legal advice.
          </p>
        </div>
        {writable && (<Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button className="bg-[#0B1F33] text-white rounded-none hover:bg-zinc-800" data-testid="add-compliance-button">
              <Plus className="h-4 w-4 mr-1" /> Archive ad
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="font-display text-2xl">Archive advertisement</DialogTitle>
            </DialogHeader>
            <div className="grid grid-cols-2 gap-4 py-2">
              <div className="col-span-2">
                <Label>Title</Label>
                <Input value={form.title} onChange={upd("title")} data-testid="compliance-title-input" className="mt-1 rounded-none" />
              </div>
              <div>
                <Label>Jurisdiction</Label>
                <Input value={form.jurisdiction} onChange={upd("jurisdiction")} className="mt-1 rounded-none" />
              </div>
              <div>
                <Label>Channel</Label>
                <Select value={form.channel} onValueChange={(v) => setForm({ ...form, channel: v })}>
                  <SelectTrigger className="mt-1 rounded-none" data-testid="compliance-channel-select"><SelectValue /></SelectTrigger>
                  <SelectContent>{CHANNELS.map((c) => (<SelectItem key={c} value={c}>{c}</SelectItem>))}</SelectContent>
                </Select>
              </div>
              <div className="col-span-2">
                <Label>Vendor</Label>
                <Select value={form.vendor_id} onValueChange={(v) => setForm({ ...form, vendor_id: v })}>
                  <SelectTrigger className="mt-1 rounded-none" data-testid="compliance-vendor-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">— None —</SelectItem>
                    {vendors.map((v) => (<SelectItem key={v.id} value={v.id}>{v.name}</SelectItem>))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Approver</Label>
                <Input value={form.approver} onChange={upd("approver")} className="mt-1 rounded-none" />
              </div>
              <div>
                <Label>Approval date</Label>
                <Input type="date" value={form.approval_date} onChange={upd("approval_date")} className="mt-1 rounded-none" />
              </div>
              <div>
                <Label>Ran from</Label>
                <Input type="date" value={form.ran_from} onChange={upd("ran_from")} className="mt-1 rounded-none" />
              </div>
              <div>
                <Label>Ran to</Label>
                <Input type="date" value={form.ran_to} onChange={upd("ran_to")} className="mt-1 rounded-none" />
              </div>
              <div className="col-span-2">
                <Label>Required disclaimers</Label>
                <Textarea value={form.disclaimers} onChange={upd("disclaimers")} rows={2} className="mt-1 rounded-none" />
              </div>
              <div className="col-span-2">
                <Label>Notes</Label>
                <Textarea value={form.notes} onChange={upd("notes")} rows={2} className="mt-1 rounded-none" />
              </div>
            </div>
            <DialogFooter>
              <Button onClick={save} className="bg-[#0B1F33] text-white rounded-none" data-testid="save-compliance-button">Archive</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>)}
      </div>

      {items.length === 0 && (
        <div className="border border-dashed border-zinc-300 p-16 text-center text-zinc-500">
          <ScrollText className="h-8 w-8 mx-auto mb-3 text-zinc-400" />
          No archived advertisements yet.
        </div>
      )}

      <div className="grid md:grid-cols-2 gap-4">
        {items.map((it) => (
          <div key={it.id} className="border border-zinc-200 bg-white p-5" data-testid={`compliance-card-${it.id}`}>
            <div className="flex items-start justify-between">
              <div>
                <p className="font-mono-data text-[10px] uppercase tracking-widest text-zinc-500">
                  {it.jurisdiction} · {it.channel}
                </p>
                <h3 className="font-display text-xl mt-1">{it.title}</h3>
              </div>
              {writable && (
              <button onClick={() => remove(it.id)} className="text-zinc-300 hover:text-[#B54747]" data-testid={`delete-compliance-${it.id}`}>
                <Trash2 className="h-4 w-4" />
              </button>
              )}
            </div>
            <div className="mt-3 space-y-1 text-sm text-zinc-600">
              <p><span className="text-zinc-400">Approved:</span> {it.approval_date?.slice(0, 10) || "—"} by {it.approver || "—"}</p>
              <p><span className="text-zinc-400">Ran:</span> {it.ran_from?.slice(0, 10) || "—"} → {it.ran_to?.slice(0, 10) || "ongoing"}</p>
              <p><span className="text-zinc-400">Vendor:</span> {vName(it.vendor_id)}</p>
              {it.disclaimers && <p className="font-mono-data text-xs text-zinc-500 border-l-2 border-zinc-200 pl-2 mt-2">{it.disclaimers}</p>}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
